'use client';

import {useState, useEffect} from 'react';
import {getCookie, setCookie, deleteCookie} from 'cookies-next';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_CONSENT_OPTIONS = {
  maxAge: 365 * 24 * 60 * 60, // 1 year
  path: '/',
  sameSite: 'lax' as const,
  secure: process.env.NODE_ENV === 'production'
};

export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(
    null
  );
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    // Load preferences from cookie on mount
    const savedPreferences = getCookie(COOKIE_CONSENT_KEY);
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences as string);
        setPreferences(parsed);
        setHasConsent(true);
      } catch (error) {
        console.error('Failed to parse cookie consent:', error);
        // Clear invalid cookie
        deleteCookie(COOKIE_CONSENT_KEY, COOKIE_CONSENT_OPTIONS);
      }
    }
  }, []);

  const updatePreferences = (newPreferences: CookiePreferences) => {
    setPreferences(newPreferences);
    setHasConsent(true);

    setCookie(
      COOKIE_CONSENT_KEY,
      JSON.stringify(newPreferences),
      COOKIE_CONSENT_OPTIONS
    );

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cookieConsentChanged'));
    }
  };

  const clearPreferences = () => {
    setPreferences(null);
    setHasConsent(false);

    deleteCookie(COOKIE_CONSENT_KEY, COOKIE_CONSENT_OPTIONS);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cookieConsentChanged'));
    }
  };

  const hasConsentFor = (category: keyof CookiePreferences): boolean => {
    if (!preferences) return false;
    return preferences[category] || false;
  };

  return {
    preferences,
    hasConsent,
    updatePreferences,
    clearPreferences,
    hasConsentFor
  };
}
